import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useListenerWebSocket } from '@/hooks/useListenerWebSocket';
import { 
  TranslationMessage, 
  TranscriptionMessage,
  SUPPORTED_LANGUAGES,
  SupportedLanguage
} from '@speech-translator/shared';
import { 
  Languages, 
  Users, 
  AlertCircle, 
  CheckCircle, 
  Volume2, 
  VolumeX,
  RefreshCw 
} from 'lucide-react';

interface ListenerRoomProps {
  roomId: string;
  initialLanguage?: SupportedLanguage;
  onLanguageChange?: (language: SupportedLanguage) => void;
}

interface Translation {
  id: string;
  originalText: string;
  translatedText: string;
  targetLanguage: string;
  timestamp: number;
  confidence?: number;
}

interface TranscriptionSegment {
  id: string;
  text: string;
  timestamp: number;
  isFinal: boolean;
}

export function ListenerRoom({ 
  roomId, 
  initialLanguage = 'en', 
  onLanguageChange 
}: ListenerRoomProps) {
  // State management
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>(initialLanguage);
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [transcriptions, setTranscriptions] = useState<TranscriptionSegment[]>([]);
  const [showOriginalText, setShowOriginalText] = useState(false);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  // Refs
  const subtitlesContainerRef = useRef<HTMLDivElement | null>(null);

  // WebSocket hook
  const webSocket = useListenerWebSocket({
    onTranslation: handleTranslation,
    onTranscription: handleTranscription,
    onError: (message) => {
      console.error('WebSocket error:', message.data);
    }
  });

  // Handle translation messages
  function handleTranslation(message: TranslationMessage) {
    const translation: Translation = {
      id: `${Date.now()}-${Math.random()}`,
      originalText: message.data.originalText,
      translatedText: message.data.translatedText,
      targetLanguage: message.data.targetLanguage,
      timestamp: message.timestamp,
      confidence: message.data.confidence
    };

    setTranslations(prev => [...prev, translation]);
    autoScrollToBottom();
  }

  // Handle transcription messages (for fallback when translation fails)
  function handleTranscription(message: TranscriptionMessage) {
    const segment: TranscriptionSegment = {
      id: `${Date.now()}-${Math.random()}`,
      text: message.data.text,
      timestamp: message.timestamp,
      isFinal: message.data.isFinal
    };

    setTranscriptions(prev => {
      if (segment.isFinal) {
        const filtered = prev.filter(s => s.isFinal);
        return [...filtered, segment];
      } else {
        const finalSegments = prev.filter(s => s.isFinal);
        return [...finalSegments, segment];
      }
    });

    autoScrollToBottom();
  }

  // Auto-scroll to bottom
  const autoScrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (subtitlesContainerRef.current) {
        subtitlesContainerRef.current.scrollTop = subtitlesContainerRef.current.scrollHeight;
      }
    }, 100);
  }, []);

  // Handle language change
  const handleLanguageChange = useCallback(async (newLanguage: SupportedLanguage) => {
    if (newLanguage === selectedLanguage) return;

    setIsChangingLanguage(true);
    try {
      await webSocket.changeLanguage(newLanguage);
      setSelectedLanguage(newLanguage);
      onLanguageChange?.(newLanguage);
      
      // Clear existing translations when changing language
      setTranslations([]);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsChangingLanguage(false);
    }
  }, [selectedLanguage, webSocket, onLanguageChange]);

  // Connect to room on mount
  useEffect(() => {
    if (roomId && webSocket.connectionStatus === 'disconnected') {
      webSocket.connect(roomId, selectedLanguage);
    }
  }, [roomId, selectedLanguage, webSocket]);

  // Get connection status badge
  const getConnectionStatusBadge = () => {
    const statusConfig = {
      connecting: { 
        variant: 'secondary' as const, 
        text: 'Connecting...', 
        icon: AlertCircle,
        className: 'animate-pulse'
      },
      connected: { 
        variant: 'default' as const, 
        text: 'Connected', 
        icon: CheckCircle,
        className: 'bg-green-500 hover:bg-green-600'
      },
      disconnected: { 
        variant: 'destructive' as const, 
        text: 'Disconnected', 
        icon: AlertCircle,
        className: ''
      },
      error: { 
        variant: 'destructive' as const, 
        text: 'Error', 
        icon: AlertCircle,
        className: 'animate-pulse'
      },
      reconnecting: { 
        variant: 'secondary' as const, 
        text: 'Reconnecting...', 
        icon: RefreshCw,
        className: 'animate-pulse'
      }
    };

    const config = statusConfig[webSocket.connectionStatus];
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className={`flex items-center gap-1 ${config.className}`}>
        <Icon className="h-3 w-3" />
        {config.text}
      </Badge>
    );
  };

  // Get display content (translations or transcriptions)
  const getDisplayContent = () => {
    if (showOriginalText || translations.length === 0) {
      return transcriptions.map(segment => ({
        id: segment.id,
        text: segment.text,
        timestamp: segment.timestamp,
        isOriginal: true,
        confidence: undefined
      }));
    }

    return translations.map(translation => ({
      id: translation.id,
      text: translation.translatedText,
      timestamp: translation.timestamp,
      isOriginal: false,
      confidence: translation.confidence,
      originalText: translation.originalText
    }));
  };

  const displayContent = getDisplayContent();

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold mb-2">Listener Room</h1>
        <p className="text-muted-foreground">
          Room ID: <span className="font-mono font-semibold">{roomId}</span>
        </p>
      </div>

      {/* Connection and Language Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Room Status</span>
            <div className="flex items-center gap-2">
              {getConnectionStatusBadge()}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>{webSocket.participantCount} participants connected</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowOriginalText(!showOriginalText)}
              >
                {showOriginalText ? (
                  <>
                    <Languages className="h-4 w-4 mr-2" />
                    Show Translation
                  </>
                ) : (
                  <>
                    <Volume2 className="h-4 w-4 mr-2" />
                    Show Original
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Language Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Translation Language</label>
            <Select
              value={selectedLanguage}
              onValueChange={handleLanguageChange}
              disabled={isChangingLanguage || webSocket.connectionStatus !== 'connected'}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select language">
                  {isChangingLanguage ? (
                    <span className="flex items-center gap-2">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Changing language...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Languages className="h-4 w-4" />
                      {SUPPORTED_LANGUAGES[selectedLanguage]}
                    </span>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => (
                  <SelectItem key={code} value={code}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {webSocket.error && (
            <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">{webSocket.error}</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2"
                onClick={() => webSocket.clearError()}
              >
                Dismiss
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Live Subtitles */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {showOriginalText ? (
              <>
                <VolumeX className="h-5 w-5" />
                Original Speech
              </>
            ) : (
              <>
                <Languages className="h-5 w-5" />
                Live Translation ({SUPPORTED_LANGUAGES[selectedLanguage]})
              </>
            )}
          </CardTitle>
          <CardDescription>
            {showOriginalText 
              ? "Displaying original speech transcription"
              : `Real-time translation to ${SUPPORTED_LANGUAGES[selectedLanguage]}`
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea 
            ref={subtitlesContainerRef}
            className="h-96 w-full rounded-md border p-4"
          >
            {displayContent.length === 0 ? (
              <div className="flex items-center justify-center h-full text-center">
                <div>
                  <Languages className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    Waiting for speech to translate...
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Make sure the speaker has started talking
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {displayContent.map((item) => (
                  <div 
                    key={item.id}
                    className={`p-4 rounded-lg border transition-colors ${
                      item.isOriginal 
                        ? 'bg-muted/30 border-muted' 
                        : 'bg-background border-border shadow-sm'
                    }`}
                    role="article"
                    aria-label={item.isOriginal ? 'Original speech' : 'Translated text'}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className={`text-base leading-relaxed ${
                          item.isOriginal ? 'italic text-muted-foreground' : 'font-medium text-foreground'
                        } contrast-more:text-black dark:contrast-more:text-white`}>
                          {item.text}
                        </p>
                        
                        {/* Show original text for translations */}
                        {!item.isOriginal && 'originalText' in item && item.originalText && (
                          <p className="text-sm text-muted-foreground mt-2 italic">
                            Original: {item.originalText}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-xs text-muted-foreground">
                          {new Date(item.timestamp).toLocaleTimeString()}
                        </span>
                        
                        {/* Confidence indicator */}
                        {item.confidence !== undefined && (
                          <Badge 
                            variant={item.confidence > 0.8 ? 'default' : item.confidence > 0.5 ? 'secondary' : 'destructive'}
                            className="text-xs"
                          >
                            {item.confidence === 0 ? 'Error' : `${Math.round(item.confidence * 100)}%`}
                          </Badge>
                        )}
                        
                        {/* Language indicator */}
                        {!item.isOriginal && (
                          <Badge variant="outline" className="text-xs">
                            {selectedLanguage.toUpperCase()}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Connection Help */}
      {webSocket.connectionStatus === 'disconnected' && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground" />
              <div>
                <h3 className="font-semibold mb-2">Connection Lost</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Unable to connect to the room. Please check your internet connection and try again.
                </p>
                <Button onClick={() => webSocket.reconnect()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reconnect
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}